<template>
  <div>
    <head>
      <meta charset="utf-8" />
      <title>AI-Powered Department Performance</title>
      <meta name="viewport" content="width=device-width, initial-scale=1.0">
      <link rel="shortcut icon" href="assets/images/favicon.png">
      <link href="assets/css/bootstrap.min.css" id="bootstrap-style" rel="stylesheet" type="text/css" />
      <link href="assets/css/icons.min.css" rel="stylesheet" type="text/css" />
      <link href="assets/css/app.min.css" id="app-style" rel="stylesheet" type="text/css" />
      <link href="assets/css/custom.css" id="app-style" rel="stylesheet" type="text/css" />
    </head>

    <body @contextmenu.prevent data-sidebar="dark">
    <NavigationBar/>
    <div id="content" class="content-minimize">
      <div class="page-content">
        <div class="container-fluid">

          <!-- Page Title -->
          <div class="row">
            <div class="col-12">
              <div class="page-title-box d-sm-flex align-items-center justify-content-between">
                <h4 class="mb-sm-0 font-size-18">
                  <i class="bx bx-brain me-2"></i>AI-POWERED DEPARTMENT PERFORMANCE ANALYTICS
                </h4>
                <div class="page-title-right">
                  <ol class="breadcrumb m-0">
                    <li class="breadcrumb-item"><a @click="gotTo('dashboard')">Dashboard</a></li>
                    <li class="breadcrumb-item active">Department Performance</li>
                  </ol>
                </div>
              </div>
            </div>
          </div>

          <!-- Financial Year Info Card -->
          <div class="row">
            <div class="col-12">
              <div class="card" :class="getHeaderGradient()">
                <div class="card-body text-white">
                  <div class="row align-items-center">
                    <div class="col-md-8">
                      <h5 class="text-white mb-2">
                        <i class="bx bx-calendar me-2"></i>Analysis Period: {{financialYear}}
                      </h5>
                      <div class="row mt-3">
                        <div class="col-md-4">
                          <p class="mb-1 text-white-50"><small><strong>Period:</strong></small></p>
                          <p class="mb-0 text-white">{{formatDate(period.start)}} to {{formatDate(period.end)}}</p>
                        </div>
                        <div class="col-md-4">
                          <p class="mb-1 text-white-50"><small><strong>Time Progress:</strong></small></p>
                          <div class="progress bg-white bg-opacity-25 mb-1" style="height: 20px;">
                            <div class="progress-bar bg-white" :style="{width: timeMetrics.percentageElapsed + '%'}">
                              <strong class="text-dark">{{timeMetrics.percentageElapsed}}%</strong>
                            </div>
                          </div>
                          <small class="text-white-50">{{timeMetrics.daysRemaining}} days remaining</small>
                        </div>
                        <div class="col-md-4">
                          <p class="mb-1 text-white-50"><small><strong>Performance Gap:</strong></small></p>
                          <h4 class="mb-0 text-white">
                            {{summary.performanceGap >= 0 ? '+' : ''}}{{summary.performanceGap}}%
                          </h4>
                          <small class="text-white-50">{{summary.performanceGap >= 0 ? 'Ahead' : 'Behind'}} schedule</small>
                        </div>
                      </div>
                    </div>
                    <div class="col-md-4 text-md-end mt-3 mt-md-0">
                      <button @click="showPeriodSelector = true" class="btn btn-light me-2">
                        <i class="bx bx-calendar me-1"></i> Change Period
                      </button>
                      <button @click="refreshData" class="btn btn-outline-light me-2" :disabled="loading">
                        <i class="bx bx-refresh" :class="{'bx-spin': loading}"></i>
                      </button>
                      <button @click="exportReport" class="btn btn-success">
                        <i class="bx bx-download me-1"></i> Export
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Period Selector Modal -->
          <div v-if="showPeriodSelector" class="modal fade show d-block" style="background: rgba(0,0,0,0.5);">
            <div class="modal-dialog modal-dialog-centered">
              <div class="modal-content">
                <div class="modal-header bg-primary text-white">
                  <h5 class="modal-title"><i class="bx bx-calendar me-2"></i>Select Analysis Period</h5>
                  <button @click="showPeriodSelector = false" class="btn-close btn-close-white"></button>
                </div>
                <div class="modal-body">
                  <div class="mb-3">
                    <label class="form-label fw-bold">Start Date</label>
                    <input type="date" class="form-control" v-model="customPeriod.start">
                  </div>
                  <div class="mb-3">
                    <label class="form-label fw-bold">End Date</label>
                    <input type="date" class="form-control" v-model="customPeriod.end">
                  </div>
                  <div class="alert alert-info">
                    <i class="bx bx-info-circle me-2"></i><strong>Default FY:</strong> {{formatDate(defaultPeriod.start)}} to {{formatDate(defaultPeriod.end)}}
                  </div>
                </div>
                <div class="modal-footer">
                  <button @click="resetToDefault" class="btn btn-light"><i class="bx bx-reset me-1"></i> Reset</button>
                  <button @click="applyCustomPeriod" class="btn btn-primary"><i class="bx bx-check me-1"></i> Apply</button>
                </div>
              </div>
            </div>
          </div>

          <!-- Summary Cards -->
          <div class="row">
            <div class="col-xl-3 col-md-6">
              <div class="card mini-stats-wid">
                <div class="card-body">
                  <div class="d-flex">
                    <div class="flex-grow-1">
                      <p class="text-muted fw-medium mb-2">Total Target</p>
                      <h4 class="mb-0">{{formatPrice(summary.totalTarget)}}</h4>
                    </div>
                    <div class="avatar-sm rounded-circle bg-primary align-self-center mini-stat-icon">
                      <span class="avatar-title rounded-circle bg-primary">
                        <i class="bx bx-target-lock font-size-24"></i>
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="col-xl-3 col-md-6">
              <div class="card mini-stats-wid">
                <div class="card-body">
                  <div class="d-flex">
                    <div class="flex-grow-1">
                      <p class="text-muted fw-medium mb-2">Total Collected</p>
                      <h4 class="mb-0">{{formatPrice(summary.totalCollected)}}</h4>
                      <p class="mb-0 mt-2" :class="getPerformanceColor(summary.overallPerformance)">
                        <i class="bx" :class="summary.overallPerformance >= 75 ? 'bx-trending-up' : 'bx-trending-down'"></i>
                        <strong>{{summary.overallPerformance}}%</strong>
                      </p>
                    </div>
                    <div class="avatar-sm rounded-circle align-self-center mini-stat-icon" :class="getPerformanceBg(summary.overallPerformance)">
                      <span class="avatar-title rounded-circle" :class="getPerformanceBg(summary.overallPerformance)">
                        <i class="bx bx-money font-size-24"></i>
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="col-xl-3 col-md-6">
              <div class="card mini-stats-wid">
                <div class="card-body">
                  <div class="d-flex">
                    <div class="flex-grow-1">
                      <p class="text-muted fw-medium mb-2">Remaining</p>
                      <h4 class="mb-0">{{formatPrice(summary.totalVariance)}}</h4>
                    </div>
                    <div class="avatar-sm rounded-circle bg-warning align-self-center mini-stat-icon">
                      <span class="avatar-title rounded-circle bg-warning">
                        <i class="bx bx-time-five font-size-24"></i>
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="col-xl-3 col-md-6">
              <div class="card mini-stats-wid" :class="counts.highRisk > 0 ? 'border-danger' : 'border-success'">
                <div class="card-body">
                  <div class="d-flex">
                    <div class="flex-grow-1">
                      <p class="text-muted fw-medium mb-2">High Risk</p>
                      <h3 class="mb-0" :class="counts.highRisk > 0 ? 'text-danger' : 'text-success'">{{counts.highRisk}}</h3>
                      <p class="mb-0 mt-2" :class="counts.highRisk > 0 ? 'text-danger' : 'text-success'">
                        <small><strong>{{counts.highRisk > 0 ? 'Urgent Action' : 'All Clear'}}</strong></small>
                      </p>
                    </div>
                    <div class="avatar-sm rounded-circle align-self-center mini-stat-icon" :class="counts.highRisk > 0 ? 'bg-danger' : 'bg-success'">
                      <span class="avatar-title rounded-circle" :class="counts.highRisk > 0 ? 'bg-danger' : 'bg-success'">
                        <i class="bx font-size-24" :class="counts.highRisk > 0 ? 'bx-error' : 'bx-check-circle'"></i>
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Performance Categories -->
          <div class="row mb-4">
            <div class="col-md-4">
              <div class="card border-success">
                <div class="card-body">
                  <div class="d-flex align-items-center">
                    <div class="avatar-sm rounded-circle bg-success me-3">
                      <span class="avatar-title"><i class="bx bx-check-circle font-size-24"></i></span>
                    </div>
                    <div class="flex-grow-1">
                      <h5 class="mb-1">Excellent</h5>
                      <p class="text-muted mb-0">≥ 75%</p>
                    </div>
                    <h3 class="mb-0 text-success">{{counts.excellent}}</h3>
                  </div>
                </div>
              </div>
            </div>
            <div class="col-md-4">
              <div class="card border-warning">
                <div class="card-body">
                  <div class="d-flex align-items-center">
                    <div class="avatar-sm rounded-circle bg-warning me-3">
                      <span class="avatar-title"><i class="bx bx-info-circle font-size-24"></i></span>
                    </div>
                    <div class="flex-grow-1">
                      <h5 class="mb-1">Good</h5>
                      <p class="text-muted mb-0">50-74%</p>
                    </div>
                    <h3 class="mb-0 text-warning">{{counts.good}}</h3>
                  </div>
                </div>
              </div>
            </div>
            <div class="col-md-4">
              <div class="card border-danger">
                <div class="card-body">
                  <div class="d-flex align-items-center">
                    <div class="avatar-sm rounded-circle bg-danger me-3">
                      <span class="avatar-title"><i class="bx bx-error-circle font-size-24"></i></span>
                    </div>
                    <div class="flex-grow-1">
                      <h5 class="mb-1">Poor</h5>
                      <p class="text-muted mb-0">&lt; 50%</p>
                    </div>
                    <h3 class="mb-0 text-danger">{{counts.poor}}</h3>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Departments Table -->
          <div class="row">
            <div class="col-12">
              <div class="card">
                <div class="card-header bg-white">
                  <div class="row align-items-center">
                    <div class="col-md-6">
                      <h4 class="card-title mb-0">
                        <i class="bx bx-brain me-2 text-primary"></i>Department Performance with AI Analytics
                      </h4>
                    </div>
                    <div class="col-md-6 text-md-end">
                      <div class="btn-group">
                        <button @click="sortBy = 'risk'" class="btn btn-sm" :class="sortBy === 'risk' ? 'btn-danger' : 'btn-outline-danger'">
                          <i class="bx bx-error me-1"></i>By Risk
                        </button>
                        <button @click="sortBy = 'performance'" class="btn btn-sm" :class="sortBy === 'performance' ? 'btn-primary' : 'btn-outline-primary'">
                          <i class="bx bx-chart me-1"></i>By Performance
                        </button>
                        <button @click="sortBy = 'forecast'" class="btn btn-sm" :class="sortBy === 'forecast' ? 'btn-info' : 'btn-outline-info'">
                          <i class="bx bx-line-chart me-1"></i>By Forecast
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                <div class="card-body">
                  <!-- Loading -->
                  <div v-if="loading" class="text-center py-5">
                    <div class="spinner-border text-primary mb-3" style="width: 3rem; height: 3rem;"></div>
                    <h5 class="text-muted">Analyzing with AI...</h5>
                  </div>

                  <!-- Table -->
                  <div v-else-if="departments.length > 0" class="table-responsive">
                    <table class="table table-hover align-middle mb-0">
                      <thead class="table-light">
                      <tr>
                        <th style="width:3%">#</th>
                        <th style="width:18%">Department</th>
                        <th class="text-center" style="width:10%">Risk</th>
                        <th class="text-end" style="width:10%">Target</th>
                        <th class="text-end" style="width:10%">Collected</th>
                        <th class="text-center" style="width:12%">Performance</th>
                        <th class="text-center" style="width:10%">Trend</th>
                        <th class="text-center" style="width:10%">Forecast</th>
                        <th class="text-center" style="width:12%">Daily Target</th>
                        <th class="text-center" style="width:5%">Action</th>
                      </tr>
                      </thead>
                      <tbody>
                      <tr v-for="(dept, index) in sortedDepartments" :key="dept.departmentID" :class="getRowClass(dept.riskLevel.level)">
                        <td><strong>{{index + 1}}</strong></td>
                        <td>
                          <h6 class="mb-0">{{dept.department}}</h6>
                          <small class="text-muted">ID: {{dept.departmentID}}</small>
                        </td>
                        <td class="text-center">
                          <span class="badge" :class="'bg-' + dept.riskLevel.color">{{dept.riskLevel.level}}</span>
                          <div><small class="text-muted">{{dept.riskLevel.score}}/100</small></div>
                        </td>
                        <td class="text-end"><strong>{{formatPrice(dept.target)}}</strong></td>
                        <td class="text-end"><strong :class="getPerformanceColor(dept.performance)">{{formatPrice(dept.collected)}}</strong></td>
                        <td class="text-center">
                          <div class="progress mb-1" style="height:18px;">
                            <div class="progress-bar" :class="getProgressBarClass(dept.performance)" :style="{width: Math.min(dept.performance, 100) + '%'}">
                              <strong style="font-size:11px;">{{dept.performance}}%</strong>
                            </div>
                          </div>
                          <span class="badge badge-sm" :class="getPerformanceBadgeClass(dept.performance)">{{dept.status}}</span>
                        </td>
                        <td class="text-center">
                          <span class="badge" :class="getTrendBadgeClass(dept.trendAnalysis.trend)">
                            <i class="bx" :class="getTrendIcon(dept.trendAnalysis.trend)"></i> {{dept.trendAnalysis.trend}}
                          </span>
                          <div><small class="text-muted">{{formatPrice(dept.trendAnalysis.velocity)}}/day</small></div>
                        </td>
                        <td class="text-center">
                          <h6 :class="getPerformanceColor(dept.forecast.projectedPerformance)">{{dept.forecast.projectedPerformance}}%</h6>
                          <span class="badge badge-sm" :class="getForecastBadgeClass(dept.forecast.scenario)">{{getForecastLabel(dept.forecast.scenario)}}</span>
                        </td>
                        <td class="text-center">
                          <strong class="text-primary">{{formatPrice(dept.requiredDailyTarget)}}</strong>
                          <div><small :class="dept.dailyTargetGap > 0 ? 'text-danger' : 'text-success'">
                            <i class="bx" :class="dept.dailyTargetGap > 0 ? 'bx-up-arrow-alt' : 'bx-check'"></i>
                            {{dept.dailyTargetGap > 0 ? '+' + formatPrice(Math.abs(dept.dailyTargetGap)) : 'On track'}}
                          </small></div>
                        </td>
                        <td class="text-center">
                          <button @click="viewDetails(dept)" class="btn btn-sm btn-primary me-1" title="View Full Details">
                            <i class="bx bx-detail"></i>
                          </button>
                          <button @click="showRecommendations(dept)" class="btn btn-sm btn-info" title="View AI Recommendations">
                            <i class="bx bx-bulb"></i>
                          </button>
                        </td>
                      </tr>
                      </tbody>
                    </table>
                  </div>

                  <!-- Empty -->
                  <div v-else class="text-center py-5">
                    <i class="bx bx-error-circle display-1 text-muted"></i>
                    <h5 class="mt-3">No Data</h5>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Recommendations Modal -->
          <div v-if="selectedDepartment" class="modal fade show d-block" style="background:rgba(0,0,0,0.7);">
            <div class="modal-dialog modal-xl modal-dialog-scrollable">
              <div class="modal-content">
                <div class="modal-header" :class="'bg-' + selectedDepartment.riskLevel.color + ' text-white'">
                  <h5 class="modal-title text-white"><i class="bx bx-brain me-2"></i>AI Recommendations: {{selectedDepartment.department}}</h5>
                  <button @click="selectedDepartment = null" class="btn-close btn-close-white"></button>
                </div>
                <div class="modal-body" style="max-height:70vh; overflow-y:auto;">

                  <!-- Risk Card -->
                  <div class="card mb-3" :class="'border-' + selectedDepartment.riskLevel.color">
                    <div class="card-header" :class="'bg-' + selectedDepartment.riskLevel.color + ' text-white'">
                      <h5 class="mb-0 text-white"><i class="bx bx-error-circle me-2"></i>Risk Assessment</h5>
                    </div>
                    <div class="card-body">
                      <h4>Risk: <span class="badge" :class="'bg-' + selectedDepartment.riskLevel.color">{{selectedDepartment.riskLevel.level}}</span></h4>
                      <div class="alert" :class="'alert-' + selectedDepartment.riskLevel.color">{{selectedDepartment.riskLevel.action}}</div>
                      <h6>Risk Factors:</h6>
                      <ul v-if="selectedDepartment.riskLevel.factors.length > 0">
                        <li v-for="(f, i) in selectedDepartment.riskLevel.factors" :key="i">{{f}}</li>
                      </ul>
                      <p v-else class="text-muted">No major risk factors</p>
                    </div>
                  </div>

                  <!-- Forecast Card -->
                  <div class="card mb-3 border-info">
                    <div class="card-header bg-info text-white">
                      <h5 class="mb-0 text-white"><i class="bx bx-line-chart me-2"></i>Forecast</h5>
                    </div>
                    <div class="card-body">
                      <div class="row text-center">
                        <div class="col-md-3">
                          <h6 class="text-muted">Current</h6>
                          <h3 :class="getPerformanceColor(selectedDepartment.performance)">{{selectedDepartment.performance}}%</h3>
                        </div>
                        <div class="col-md-3">
                          <h6 class="text-muted">Projected</h6>
                          <h3 :class="getPerformanceColor(selectedDepartment.forecast.projectedPerformance)">{{selectedDepartment.forecast.projectedPerformance}}%</h3>
                        </div>
                        <div class="col-md-3">
                          <h6 class="text-muted">Shortfall</h6>
                          <h3 :class="selectedDepartment.forecast.projectedShortfall > 0 ? 'text-danger' : 'text-success'">{{formatPrice(Math.abs(selectedDepartment.forecast.projectedShortfall))}}</h3>
                        </div>
                        <div class="col-md-3">
                          <h6 class="text-muted">Confidence</h6>
                          <h3 class="text-info">{{selectedDepartment.forecast.confidenceScore}}%</h3>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Recommendations -->
                  <div class="card">
                    <div class="card-header bg-success text-white">
                      <h5 class="mb-0 text-white"><i class="bx bx-bulb me-2"></i>Action Plan</h5>
                    </div>
                    <div class="card-body">
                      <div v-for="(rec, idx) in selectedDepartment.recommendations" :key="idx" class="mb-4">
                        <div class="d-flex">
                          <div class="avatar-sm me-3">
                            <span class="avatar-title rounded-circle" :class="getPriorityBadgeClass(rec.priority)">{{rec.priority}}</span>
                          </div>
                          <div class="flex-grow-1">
                            <div class="d-flex justify-content-between mb-2">
                              <h5>{{rec.title}}</h5>
                              <span class="badge" :class="getCategoryBadgeClass(rec.category)">{{rec.category}}</span>
                            </div>
                            <p class="text-muted">{{rec.message}}</p>
                            <h6>Actions:</h6>
                            <ul>
                              <li v-for="(a, ai) in rec.actions" :key="ai">{{a}}</li>
                            </ul>
                          </div>
                        </div>
                        <hr v-if="idx < selectedDepartment.recommendations.length - 1">
                      </div>
                    </div>
                  </div>

                </div>
                <div class="modal-footer">
                  <button @click="exportDepartmentReport(selectedDepartment)" class="btn btn-success">
                    <i class="bx bx-download me-1"></i> Export
                  </button>
                  <button @click="selectedDepartment = null" class="btn btn-secondary">Close</button>
                </div>
              </div>
            </div>
          </div>

          <!-- Overall County Performance Trends -->
          <div class="row mt-4">
            <div class="col-12">
              <div class="card">
                <div class="card-header bg-white">
                  <div class="row align-items-center">
                    <div class="col-md-6">
                      <h4 class="card-title mb-0">
                        <i class="bx bx-trending-up me-2 text-primary"></i>Overall County Performance Trends
                      </h4>
                      <p class="text-muted mb-0 mt-1"><small>All departments combined - Track if we are improving or declining</small></p>
                    </div>
                    <div class="col-md-6 text-md-end">
                      <div class="btn-group btn-group-sm">
                        <button @click="activeOverviewTab = 'daily'" class="btn" :class="activeOverviewTab === 'daily' ? 'btn-primary' : 'btn-outline-primary'">
                          <i class="bx bx-calendar me-1"></i>Daily
                        </button>
                        <button @click="activeOverviewTab = 'weekly'" class="btn" :class="activeOverviewTab === 'weekly' ? 'btn-primary' : 'btn-outline-primary'">
                          <i class="bx bx-calendar-week me-1"></i>Weekly
                        </button>
                        <button @click="activeOverviewTab = 'monthly'" class="btn" :class="activeOverviewTab === 'monthly' ? 'btn-primary' : 'btn-outline-primary'">
                          <i class="bx bx-calendar-alt me-1"></i>Monthly
                        </button>
                        <button @click="activeOverviewTab = 'yearly'" class="btn" :class="activeOverviewTab === 'yearly' ? 'btn-primary' : 'btn-outline-primary'">
                          <i class="bx bx-bar-chart me-1"></i>Yearly
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
                <div class="card-body">
                  <!-- Daily Trend -->
                  <div v-show="activeOverviewTab === 'daily'">
                    <div id="daily-overview-chart" style="height: 400px;"></div>
                    <div v-if="overviewAI.daily" class="alert alert-info mt-3">
                      <i class="bx bx-brain me-2"></i><strong>AI Insight:</strong> {{overviewAI.daily}}
                    </div>
                  </div>

                  <!-- Weekly Trend -->
                  <div v-show="activeOverviewTab === 'weekly'">
                    <div id="weekly-overview-chart" style="height: 400px;"></div>
                    <div v-if="overviewAI.weekly" class="alert alert-info mt-3">
                      <i class="bx bx-brain me-2"></i><strong>AI Insight:</strong> {{overviewAI.weekly}}
                    </div>
                  </div>

                  <!-- Monthly Trend -->
                  <div v-show="activeOverviewTab === 'monthly'">
                    <div id="monthly-overview-chart" style="height: 400px;"></div>
                    <div v-if="overviewAI.monthly" class="alert alert-info mt-3">
                      <i class="bx bx-brain me-2"></i><strong>AI Insight:</strong> {{overviewAI.monthly}}
                    </div>
                  </div>

                  <!-- Yearly Trend -->
                  <div v-show="activeOverviewTab === 'yearly'">
                    <div id="yearly-overview-chart" style="height: 400px;"></div>
                    <div v-if="overviewAI.yearly" class="alert alert-info mt-3">
                      <i class="bx bx-brain me-2"></i><strong>AI Insight:</strong> {{overviewAI.yearly}}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>

      <footer class="footer">
        <div class="container-fluid">
          <div class="row">
            <div class="col-sm-6">©{{new Date().getFullYear()}} County Revenue System</div>
            <div class="col-sm-6 text-sm-end">AI-Powered Department Performance</div>
          </div>
        </div>
      </footer>
    </div>
    </body>
  </div>
</template>

<script>
/* eslint-disable */
import NavigationBar from "@/components/Navigation";
import router from "@/router";
import {biller, execute2} from "@/api";
import moment from 'moment';
import Highcharts from 'highcharts';

export default {
  name: "DepartmentPerformanceAI",
  components: {NavigationBar},

  data() {
    return {
      loading: false,
      departments: [],
      summary: {totalTarget:0, totalCollected:0, totalVariance:0, overallPerformance:0, performanceGap:0},
      counts: {excellent:0, good:0, poor:0, highRisk:0},
      timeMetrics: {totalDays:0, daysPassed:0, daysRemaining:0, percentageElapsed:0},
      period: {start:'', end:''},
      defaultPeriod: {start:'', end:''},
      customPeriod: {start:'', end:''},
      sortBy: 'risk',
      selectedDepartment: null,
      showPeriodSelector: false,

      // Trend data
      activeOverviewTab: 'daily',
      dailyOverview: [],
      weeklyOverview: [],
      monthlyOverview: [],
      yearlyOverview: [],
      overviewAI: {
        daily: '',
        weekly: '',
        monthly: '',
        yearly: ''
      },
      financialYearStart: '',
      financialYearEnd: ''
    }
  },

  computed: {
    financialYear() {
      if (!this.period.start || !this.period.end) return '';
      return moment(this.period.start).format('YYYY') + '/' + moment(this.period.end).format('YYYY');
    },

    sortedDepartments() {
      if (!this.departments || this.departments.length === 0) return [];
      const sorted = [...this.departments];
      if (this.sortBy === 'risk') {
        const order = {'CRITICAL': 4, 'HIGH': 3, 'MEDIUM': 2, 'LOW': 1};
        return sorted.sort((a, b) => (order[b.riskLevel.level] || 0) - (order[a.riskLevel.level] || 0));
      } else if (this.sortBy === 'performance') {
        return sorted.sort((a, b) => a.performance - b.performance);
      } else if (this.sortBy === 'forecast') {
        return sorted.sort((a, b) => a.forecast.projectedPerformance - b.forecast.projectedPerformance);
      }
      return sorted;
    }
  },

  watch: {
    activeOverviewTab(newTab) {
      this.$nextTick(() => {
        if (newTab === 'daily' && this.dailyOverview.length > 0) {
          this.createDailyOverviewChart();
        } else if (newTab === 'weekly' && this.weeklyOverview.length > 0) {
          this.createWeeklyOverviewChart();
        } else if (newTab === 'monthly' && this.monthlyOverview.length > 0) {
          this.createMonthlyOverviewChart();
        } else if (newTab === 'yearly' && this.yearlyOverview.length > 0) {
          this.createYearlyOverviewChart();
        }
      });
    }
  },

  mounted() {
    this.getCountyInfo();
    this.menuAccess();
  },

  methods: {
    menuAccess() {
      const data = new FormData();
      data.append("function", "menuAccess");
      data.append("menu", "Department Performance");
      data.append("sessionNames", sessionStorage.getItem("sessionNames"));
      data.append("sessionIdNo", sessionStorage.getItem("sessionIdNo"));
      execute2(data, biller).then(() => {}).catch(() => {});
    },

    getCountyInfo() {
      const data = new FormData();
      data.append("function", "getCountyInfo");
      execute2(data, biller)
          .then((res) => {
            if (res.data.success && res.data.data.county) {
              const c = res.data.data.county;

              // Format dates for date input (YYYY-MM-DD)
              const formatForInput = (datetime) => {
                if (!datetime) return '';
                return datetime.split(' ')[0]; // Gets just the date part (YYYY-MM-DD)
              };

              this.financialYearStart = formatForInput(c.financialYearStart);
              this.financialYearEnd = formatForInput(c.financialYearEnd);

              this.defaultPeriod = {
                start: this.financialYearStart,
                end: this.financialYearEnd
              };
              this.customPeriod = {...this.defaultPeriod};

              // Load everything
              this.getDepartmentPerformance();
              this.getOverviewTrends();
            }
          })
          .catch((e) => {
            console.error("Error loading county info:", e);
          });
    },

    getDepartmentPerformance() {
      this.loading = true;
      const data = new FormData();
      data.append("function", "getDepartmentsPerformanceWithAI");
      if (this.customPeriod.start && this.customPeriod.end) {
        data.append("financialYearStart", this.customPeriod.start);
        data.append("financialYearEnd", this.customPeriod.end);
      }

      execute2(data, biller)
          .then((res) => {
            this.loading = false;
            console.log("API Response:", res.data);

            // Handle nested data structure - API returns res.data.data.data
            const responseData = res.data.data?.data || res.data.data;

            if (res.data.success && responseData) {
              this.departments = responseData.departments || [];
              this.summary = responseData.summary || this.summary;
              this.counts = responseData.counts || this.counts;
              this.timeMetrics = responseData.timeMetrics || this.timeMetrics;
              this.period = responseData.period || this.period;

              console.log("Departments loaded:", this.departments.length);
            }
          })
          .catch((e) => {
            this.loading = false;
            console.error("Error:", e);
          });
    },

    getOverviewTrends() {
      this.getDailyOverview();
      this.getWeeklyOverview();
      this.getMonthlyOverview();
      this.getYearlyOverview();
    },

    getDailyOverview() {
      const data = new FormData();
      data.append("function", "getOverallDailyTrend");
      data.append("days", 30);

      execute2(data, biller)
          .then((res) => {
            console.log("Daily trend response:", res.data);
            if (res.data.success) {
              // ✅ FIXED: Extract from res.data.data.trend (nested structure)
              this.dailyOverview = res.data.data?.trend || res.data.trend || [];
              console.log("Daily overview loaded:", this.dailyOverview.length, "days");
              this.overviewAI.daily = this.analyzeDailyTrend(this.dailyOverview);
              this.$nextTick(() => {
                if (this.activeOverviewTab === 'daily') {
                  this.createDailyOverviewChart();
                }
              });
            }
          })
          .catch((e) => console.error("Error loading daily overview:", e));
    },

    getWeeklyOverview() {
      const data = new FormData();
      data.append("function", "getOverallWeeklyTrend");
      data.append("weeks", 12);

      execute2(data, biller)
          .then((res) => {
            console.log("Weekly trend response:", res.data);
            if (res.data.success) {
              // ✅ FIXED: Extract from res.data.data.trend (nested structure)
              this.weeklyOverview = res.data.data?.trend || res.data.trend || [];
              console.log("Weekly overview loaded:", this.weeklyOverview.length, "weeks");
              this.overviewAI.weekly = this.analyzeWeeklyTrend(this.weeklyOverview);
              this.$nextTick(() => {
                if (this.activeOverviewTab === 'weekly') {
                  this.createWeeklyOverviewChart();
                }
              });
            }
          })
          .catch((e) => console.error("Error loading weekly overview:", e));
    },

    getMonthlyOverview() {
      const data = new FormData();
      data.append("function", "getOverallMonthlyTrend");
      data.append("months", 12);

      execute2(data, biller)
          .then((res) => {
            console.log("Monthly trend response:", res.data);
            if (res.data.success) {
              // ✅ FIXED: Extract from res.data.data.trend (nested structure)
              this.monthlyOverview = res.data.data?.trend || res.data.trend || [];
              console.log("Monthly overview loaded:", this.monthlyOverview.length, "months");
              this.overviewAI.monthly = this.analyzeMonthlyTrend(this.monthlyOverview);
              this.$nextTick(() => {
                if (this.activeOverviewTab === 'monthly') {
                  this.createMonthlyOverviewChart();
                }
              });
            }
          })
          .catch((e) => console.error("Error loading monthly overview:", e));
    },

    getYearlyOverview() {
      const data = new FormData();
      data.append("function", "getOverallYearlyTrend");
      if (this.financialYearStart && this.financialYearStart !== 'undefined') {
        data.append("financialYearStart", this.financialYearStart);
      }
      if (this.financialYearEnd && this.financialYearEnd !== 'undefined') {
        data.append("financialYearEnd", this.financialYearEnd);
      }

      execute2(data, biller)
          .then((res) => {
            console.log("Yearly trend response:", res.data);
            if (res.data.success) {
              // ✅ FIXED: Extract from res.data.data.trend (nested structure)
              this.yearlyOverview = res.data.data?.trend || res.data.trend || [];
              const totalTarget = res.data.data?.totalTarget || res.data.totalTarget || 0;
              console.log("Yearly overview loaded:", this.yearlyOverview.length, "months");
              this.overviewAI.yearly = this.analyzeYearlyTrend(this.yearlyOverview, totalTarget);
              this.$nextTick(() => {
                if (this.activeOverviewTab === 'yearly') {
                  this.createYearlyOverviewChart();
                }
              });
            }
          })
          .catch((e) => console.error("Error loading yearly overview:", e));
    },

    // Chart creation methods
    createDailyOverviewChart() {
      const labels = this.dailyOverview.map(d => d.label);
      const amounts = this.dailyOverview.map(d => d.amount);

      Highcharts.chart('daily-overview-chart', {
        chart: { type: 'area' },
        title: { text: 'Last 30 Days Performance' },
        xAxis: { categories: labels, title: { text: 'Date' } },
        yAxis: { title: { text: 'Amount (KES)' } },
        series: [{
          name: 'Daily Collections',
          data: amounts,
          color: '#5b73e8'
        }],
        credits: { enabled: false }
      });
    },

    createWeeklyOverviewChart() {
      const labels = this.weeklyOverview.map(w => w.label);
      const amounts = this.weeklyOverview.map(w => w.amount);

      Highcharts.chart('weekly-overview-chart', {
        chart: { type: 'column' },
        title: { text: 'Last 12 Weeks Performance' },
        xAxis: { categories: labels, title: { text: 'Week' } },
        yAxis: { title: { text: 'Amount (KES)' } },
        series: [{
          name: 'Weekly Collections',
          data: amounts,
          color: '#34c38f'
        }],
        credits: { enabled: false }
      });
    },

    createMonthlyOverviewChart() {
      const labels = this.monthlyOverview.map(m => m.label);
      const amounts = this.monthlyOverview.map(m => m.amount);

      Highcharts.chart('monthly-overview-chart', {
        chart: { type: 'line' },
        title: { text: 'Last 12 Months Performance' },
        xAxis: { categories: labels, title: { text: 'Month' } },
        yAxis: { title: { text: 'Amount (KES)' } },
        series: [{
          name: 'Monthly Collections',
          data: amounts,
          color: '#f1b44c'
        }],
        credits: { enabled: false }
      });
    },

    createYearlyOverviewChart() {
      const labels = this.yearlyOverview.map(y => y.label);
      const amounts = this.yearlyOverview.map(y => y.amount);

      // Calculate cumulative
      const cumulative = [];
      let sum = 0;
      for (let amount of amounts) {
        sum += amount;
        cumulative.push(sum);
      }

      Highcharts.chart('yearly-overview-chart', {
        chart: { type: 'area' },
        title: { text: 'Financial Year Performance (Cumulative)' },
        xAxis: { categories: labels, title: { text: 'Month' } },
        yAxis: { title: { text: 'Amount (KES)' } },
        series: [{
          name: 'Cumulative Collections',
          data: cumulative,
          color: '#50a5f1'
        }],
        credits: { enabled: false }
      });
    },

    // AI Analysis methods
    analyzeDailyTrend(data) {
      if (!data || data.length < 7) return 'Insufficient data for analysis';

      const amounts = data.map(d => d.amount);
      const avg = amounts.reduce((a, b) => a + b, 0) / amounts.length;
      const last7 = amounts.slice(-7);
      const last7Avg = last7.reduce((a, b) => a + b, 0) / 7;

      if (last7Avg > avg * 1.1) {
        return `Strong upward trend! Last 7 days average (${this.formatPrice(last7Avg)}) is 10% higher than 30-day average.`;
      } else if (last7Avg < avg * 0.9) {
        return `Declining trend detected. Last 7 days average (${this.formatPrice(last7Avg)}) is 10% lower than 30-day average. Action needed.`;
      } else {
        return `Stable performance. Daily average: ${this.formatPrice(avg)}. Keep monitoring.`;
      }
    },

    analyzeWeeklyTrend(data) {
      if (!data || data.length < 4) return 'Insufficient data for analysis';

      const amounts = data.map(w => w.amount);
      const last4 = amounts.slice(-4);
      const prev4 = amounts.slice(-8, -4);

      if (prev4.length < 4) return 'Building trend data...';

      const last4Avg = last4.reduce((a, b) => a + b, 0) / 4;
      const prev4Avg = prev4.reduce((a, b) => a + b, 0) / 4;

      const change = ((last4Avg - prev4Avg) / prev4Avg) * 100;

      if (change > 5) {
        return `Positive momentum! Collections increased by ${change.toFixed(1)}% in last 4 weeks.`;
      } else if (change < -5) {
        return `Warning: Collections dropped by ${Math.abs(change).toFixed(1)}% in last 4 weeks. Review strategies.`;
      } else {
        return `Steady weekly performance. Week-over-week change: ${change.toFixed(1)}%.`;
      }
    },

    analyzeMonthlyTrend(data) {
      if (!data || data.length < 3) return 'Insufficient data for analysis';

      const amounts = data.map(m => m.amount);
      const lastMonth = amounts[amounts.length - 1];
      const prevMonth = amounts[amounts.length - 2];

      const change = ((lastMonth - prevMonth) / prevMonth) * 100;

      if (change > 10) {
        return `Excellent growth! Last month increased by ${change.toFixed(1)}% compared to previous month.`;
      } else if (change < -10) {
        return `Significant decline of ${Math.abs(change).toFixed(1)}% last month. Urgent review required.`;
      } else {
        return `Normal monthly variation. Month-over-month change: ${change.toFixed(1)}%.`;
      }
    },

    analyzeYearlyTrend(data, target) {
      if (!data || data.length === 0) return 'No yearly data available';

      const amounts = data.map(y => y.amount);
      const totalCollected = amounts.reduce((a, b) => a + b, 0);
      const performance = target > 0 ? ((totalCollected / target) * 100).toFixed(1) : 0;

      const monthsPassed = amounts.filter(a => a > 0).length;
      const monthsRemaining = 12 - monthsPassed;

      if (performance >= 75) {
        return `On track! ${performance}% achieved with ${monthsRemaining} months remaining. Maintain momentum.`;
      } else if (performance >= 50) {
        return `Moderate progress at ${performance}%. Need to accelerate in remaining ${monthsRemaining} months.`;
      } else {
        return `Critical: Only ${performance}% achieved. ${monthsRemaining} months left. Urgent intervention required.`;
      }
    },

    refreshData() {
      this.getDepartmentPerformance();
      this.getOverviewTrends();
    },

    applyCustomPeriod() {
      if (!this.customPeriod.start || !this.customPeriod.end) return;
      this.showPeriodSelector = false;
      this.getDepartmentPerformance();
    },

    resetToDefault() {
      this.customPeriod = {...this.defaultPeriod};
      this.showPeriodSelector = false;
      this.getDepartmentPerformance();
    },

    showRecommendations(dept) { this.selectedDepartment = dept; },

    viewDetails(dept) {
      // Store department info in session storage for the details page
      sessionStorage.setItem('selectedDepartmentID', dept.departmentID);
      sessionStorage.setItem('selectedDepartmentName', dept.department);
      sessionStorage.setItem('financialYearStart', this.period.start);
      sessionStorage.setItem('financialYearEnd', this.period.end);

      // Navigate to details page
      this.gotTo('department-performance-details');
    },

    exportReport() {
      const headers = [
        '#', 'Department', 'Dept ID', 'Risk Level', 'Risk Score',
        'Target', 'Collected', 'Performance %', 'Status',
        'Trend', 'Velocity', 'Projected Performance', 'Forecast Scenario',
        'Required Daily Target', 'Avg Daily Collection', 'Daily Gap'
      ];

      const rows = this.sortedDepartments.map((dept, index) => [
        index + 1,
        `"${dept.department}"`,
        dept.departmentID,
        dept.riskLevel.level,
        dept.riskLevel.score,
        dept.target,
        dept.collected,
        dept.performance,
        dept.status,
        dept.trendAnalysis.trend,
        dept.trendAnalysis.velocity,
        dept.forecast.projectedPerformance,
        dept.forecast.scenario,
        dept.requiredDailyTarget,
        dept.averageDailyCollection,
        dept.dailyTargetGap
      ]);

      const metadata = [
        ['AI-POWERED DEPARTMENT PERFORMANCE REPORT'],
        ['Generated:', moment().format('DD/MM/YYYY HH:mm:ss')],
        ['Period:', `${this.formatDate(this.period.start)} to ${this.formatDate(this.period.end)}`],
        ['Financial Year:', this.financialYear],
        [],
        ['SUMMARY'],
        ['Total Target:', this.summary.totalTarget],
        ['Total Collected:', this.summary.totalCollected],
        ['Overall Performance:', `${this.summary.overallPerformance}%`],
        ['Performance Gap:', `${this.summary.performanceGap}%`],
        ['Days Remaining:', this.timeMetrics.daysRemaining],
        ['Progress:', `${this.timeMetrics.percentageElapsed}%`],
        [],
        ['DEPARTMENT COUNTS'],
        ['Excellent Performers:', this.counts.excellent],
        ['Good Performers:', this.counts.good],
        ['Needs Improvement:', this.counts.poor],
        ['High Risk:', this.counts.highRisk],
        [],
        []
      ];

      const allRows = [...metadata, headers, ...rows];
      const csvContent = allRows.map(row => row.join(',')).join('\n');
      const BOM = '\uFEFF';
      const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);

      link.href = url;
      link.download = `Department_Performance_AI_${moment().format('YYYYMMDD_HHmmss')}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      setTimeout(() => URL.revokeObjectURL(url), 100);

      this.$swal({
        icon: 'success',
        title: 'Export Successful!',
        text: 'Department performance report has been downloaded',
        timer: 2000,
        showConfirmButton: false
      });
    },

    exportDepartmentReport(dept) {
      const lines = [
        `AI PERFORMANCE REPORT: ${dept.department}`,
        `Generated: ${moment().format('DD/MM/YYYY HH:mm:ss')}`,
        `Department ID: ${dept.departmentID}`,
        ``,
        `=== RISK ASSESSMENT ===`,
        `Risk Level: ${dept.riskLevel.level}`,
        `Risk Score: ${dept.riskLevel.score}/100`,
        `Action Required: ${dept.riskLevel.action}`,
        ``,
        `Risk Factors:`,
        ...dept.riskLevel.factors.map(f => `  - ${f}`),
        ``,
        `=== PERFORMANCE METRICS ===`,
        `Target: KES ${dept.target.toLocaleString()}`,
        `Collected: KES ${dept.collected.toLocaleString()}`,
        `Performance: ${dept.performance}%`,
        `Status: ${dept.status}`,
        `Variance: KES ${dept.variance.toLocaleString()}`,
        ``,
        `=== DAILY TARGETS ===`,
        `Average Daily Collection: KES ${dept.averageDailyCollection.toLocaleString()}`,
        `Required Daily Target: KES ${dept.requiredDailyTarget.toLocaleString()}`,
        `Daily Target Gap: KES ${dept.dailyTargetGap.toLocaleString()}`,
        ``,
        `=== TREND ANALYSIS ===`,
        `Trend: ${dept.trendAnalysis.trend}`,
        `Velocity: KES ${dept.trendAnalysis.velocity.toLocaleString()}/day`,
        `Consistency: ${dept.trendAnalysis.consistency}%`,
        `Volatility: ${dept.trendAnalysis.volatility}%`,
        ``,
        `=== FORECAST ===`,
        `Projected Collection: KES ${dept.forecast.projectedCollection.toLocaleString()}`,
        `Projected Performance: ${dept.forecast.projectedPerformance}%`,
        `Projected Shortfall: KES ${Math.abs(dept.forecast.projectedShortfall).toLocaleString()}`,
        `Scenario: ${dept.forecast.scenario}`,
        `Confidence Score: ${dept.forecast.confidenceScore}%`,
        ``,
        `=== AI-GENERATED RECOMMENDATIONS ===`,
        ...dept.recommendations.map((rec, i) => {
          return [
            ``,
            `${i + 1}. ${rec.title} [Priority ${rec.priority} - ${rec.category}]`,
            `   ${rec.message}`,
            `   `,
            `   Recommended Actions:`,
            ...rec.actions.map(a => `     • ${a}`)
          ].join('\n');
        })
      ];

      const content = lines.join('\n');
      const blob = new Blob([content], { type: 'text/plain;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `${dept.department.replace(/\s+/g, '_')}_AI_Report_${moment().format('YYYYMMDD_HHmmss')}.txt`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      this.$swal({
        icon: 'success',
        title: 'Export Successful!',
        text: `${dept.department} report has been downloaded`,
        timer: 2000,
        showConfirmButton: false
      });
    },

    formatPrice(v) {
      if (!v && v !== 0) return 'KES 0';
      return new Intl.NumberFormat('en-KE', {style:'currency', currency:'KES', minimumFractionDigits:0, maximumFractionDigits:0}).format(v);
    },
    formatDate(d) { return d ? moment(d).format('DD MMM YYYY') : ''; },
    getPerformanceColor(v) { return v >= 75 ? 'text-success' : v >= 50 ? 'text-warning' : 'text-danger'; },
    getPerformanceBg(v) { return v >= 75 ? 'bg-success' : v >= 50 ? 'bg-warning' : 'bg-danger'; },
    getProgressBarClass(v) { return v >= 75 ? 'bg-success' : v >= 50 ? 'bg-warning' : 'bg-danger'; },
    getPerformanceBadgeClass(v) { return v >= 75 ? 'badge-soft-success' : v >= 50 ? 'badge-soft-warning' : 'badge-soft-danger'; },
    getRowClass(r) { return r === 'CRITICAL' ? 'table-danger' : r === 'HIGH' ? 'table-warning' : ''; },
    getHeaderGradient() { return this.summary.overallPerformance >= 75 ? 'bg-success' : this.summary.overallPerformance >= 50 ? 'bg-warning' : 'bg-danger'; },
    getRiskIcon(l) { const i = {'CRITICAL':'bx-error-circle','HIGH':'bx-error','MEDIUM':'bx-info-circle','LOW':'bx-check-circle'}; return i[l] || 'bx-info-circle'; },
    getRiskColor(l) { const c = {'CRITICAL':'#dc3545','HIGH':'#fd7e14','MEDIUM':'#ffc107','LOW':'#28a745'}; return c[l] || '#6c757d'; },
    getTrendIcon(t) { return t === 'IMPROVING' ? 'bx-trending-up' : t === 'DECLINING' ? 'bx-trending-down' : 'bx-minus'; },
    getTrendBadgeClass(t) { return t === 'IMPROVING' ? 'badge-soft-success' : t === 'DECLINING' ? 'badge-soft-danger' : 'badge-soft-secondary'; },
    getForecastBadgeClass(s) { return s === 'ON_TRACK_TO_EXCEED' ? 'badge-soft-success' : s === 'LIKELY_TO_MEET_TARGET' ? 'badge-soft-info' : s === 'MAY_STRUGGLE' ? 'badge-soft-warning' : 'badge-soft-danger'; },
    getForecastLabel(s) { const l = {'ON_TRACK_TO_EXCEED':'Will Exceed','LIKELY_TO_MEET_TARGET':'On Track','MAY_STRUGGLE':'May Struggle','UNLIKELY_TO_MEET_TARGET':'Unlikely','INSUFFICIENT_DATA':'No Data'}; return l[s] || s; },
    getPriorityBadgeClass(p) { return p === 1 ? 'bg-danger text-white' : p === 2 ? 'bg-warning' : 'bg-info text-white'; },
    getCategoryBadgeClass(c) { const cl = {'URGENT':'bg-danger text-white','HIGH':'bg-warning','TARGET':'bg-primary text-white','TREND':'bg-info text-white','MAINTAIN':'bg-success text-white'}; return cl[c] || 'bg-secondary text-white'; },
    gotTo(r) { router.push(r); }
  }
}
</script>

<style scoped>
.bx-spin { animation: spin 1s linear infinite; }
@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
.modal.show { display: block !important; }
.badge-sm { font-size: 0.75rem; padding: 0.25rem 0.5rem; }
.badge-soft-success { background-color: rgba(25,135,84,0.1); color: #198754; }
.badge-soft-warning { background-color: rgba(255,193,7,0.1); color: #ffc107; }
.badge-soft-danger { background-color: rgba(220,53,69,0.1); color: #dc3545; }
.badge-soft-info { background-color: rgba(13,202,240,0.1); color: #0dcaf0; }
.badge-soft-secondary { background-color: rgba(108,117,125,0.1); color: #6c757d; }
.table-danger { background-color: rgba(220,53,69,0.1); }
.table-warning { background-color: rgba(255,193,7,0.1); }
.mini-stats-wid { border-radius: 0.5rem; transition: all 0.3s; }
.mini-stats-wid:hover { transform: translateY(-2px); box-shadow: 0 0.25rem 1rem rgba(0,0,0,0.1); }
.avatar-sm { height: 3rem; width: 3rem; }
.avatar-title { align-items: center; display: flex; height: 100%; justify-content: center; width: 100%; }
.mini-stat-icon { width: 4rem; height: 4rem; }
</style>